import React, { useEffect, useState } from 'react';
import { fetchCardDetail } from './adminApi';

function money(value) {
  if (value === null || value === undefined) return '—';
  return `$${Number(value).toFixed(2)}`;
}

function rate(value) {
  if (value === null || value === undefined) return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
}

function plain(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// The COIN score is weighted_average / (raw_price + grading_fee). Showing the
// arithmetic rather than the result is the whole point of this view: it is how
// an operator tells "the formula is fine, the input is junk" from "the formula
// is wrong".
function CoinMath({ inputs }) {
  if (!inputs) return null;
  const terms = inputs.weighted_average_terms || {};
  return (
    <div className="admin-coin-math">
      <div className="admin-coin-row">
        <span className="admin-coin-label">PSA 10 price used</span>
        <span>
          {money(inputs.psa10_price)}
          {inputs.psa10_price_source && (
            <em className="admin-coin-source"> from {inputs.psa10_price_source}</em>
          )}
        </span>
      </div>
      <div className="admin-coin-row">
        <span className="admin-coin-label">30d / 90d PSA 10</span>
        <span>{money(inputs.price_30d)} / {money(inputs.price_90d)}</span>
      </div>
      <div className="admin-coin-row">
        <span className="admin-coin-label">Sale counts (30d / 90d)</span>
        <span>{plain(inputs.price_count_30d)} / {plain(inputs.price_count_90d)}</span>
      </div>
      <div className="admin-coin-row">
        <span className="admin-coin-label">Gem rate / PSA 9 rate</span>
        <span>{rate(inputs.card_gem_rate)} / {rate(inputs.psa9_grade_rate)}</span>
      </div>
      <div className="admin-coin-row">
        <span className="admin-coin-label">Population (gems / total)</span>
        <span>{plain(inputs.card_gems)} / {plain(inputs.card_total_grades)}</span>
      </div>
      <hr className="admin-coin-rule" />
      <div className="admin-coin-row">
        <span className="admin-coin-label">Weighted average</span>
        <span>
          {money(terms.psa10)} + {money(terms.psa9)} + {money(terms.raw)}
          {' = '}
          <strong>{money(inputs.weighted_average)}</strong>
        </span>
      </div>
      <div className="admin-coin-row">
        <span className="admin-coin-label">Cost basis</span>
        <span>
          {money(inputs.raw_price)} raw + {money(inputs.grading_fee)} fee
          {' = '}
          <strong>{money(inputs.cost_basis)}</strong>
        </span>
      </div>
      <div className="admin-coin-row admin-coin-row--result">
        <span className="admin-coin-label">COIN score</span>
        <span>
          {money(inputs.weighted_average)} ÷ {money(inputs.cost_basis)}
          {' = '}
          <strong>{plain(inputs.coin_score_stored)}</strong>
          {inputs.coin_score_recomputed !== null
            && inputs.coin_score_stored !== null
            && Math.abs(inputs.coin_score_recomputed - inputs.coin_score_stored) > 0.02 && (
            <em className="admin-coin-mismatch">
              {' '}recomputes to {inputs.coin_score_recomputed} — stored value is stale
            </em>
          )}
        </span>
      </div>
      <div className="admin-coin-row">
        <span className="admin-coin-label">Volatility / multiplier</span>
        <span>{plain(inputs.volatility_score)} / {plain(inputs.value_multiplier)}</span>
      </div>
    </div>
  );
}

function RowTable({ row }) {
  const keys = Object.keys(row || {}).sort();
  return (
    <div className="admin-table-scroll">
      <table className="admin-table admin-table--compact">
        <tbody>
          {keys.map((key) => (
            <tr key={key}>
              <th scope="row">{key}</th>
              <td>{plain(row[key])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// A native <details> rather than a state-driven panel: the browser handles the
// collapse and the keyboard affordance for free, and a card can appear on ten
// tables at once, so most of them need to start closed.
function SourceSection({ entry, defaultOpen }) {
  const [showAll, setShowAll] = useState(false);
  return (
    <details className="admin-source" open={defaultOpen}>
      <summary className="admin-source-header">
        <span className="admin-source-title">
          {entry.list_label}
          {entry.fee_label ? ` · ${entry.fee_label}` : ''}
        </span>
        <code>{entry.source_table}</code>
      </summary>
      <CoinMath inputs={entry.coin_inputs} />
      <button type="button" className="admin-button admin-button--link" onClick={() => setShowAll((v) => !v)}>
        {showAll ? 'Hide all columns' : 'Show all columns'}
      </button>
      {showAll && <RowTable row={entry.row} />}
    </details>
  );
}

function AdminCardDetail({ cardId, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetchCardDetail(cardId, controller.signal)
      .then(setDetail)
      .catch((fetchError) => {
        if (fetchError.name !== 'AbortError') setError(fetchError.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [cardId]);

  if (loading) return <p className="admin-meta-line">Loading raw data…</p>;
  if (error) return <p className="admin-error" role="alert">{error}</p>;
  if (!detail) return null;

  const identity = detail.identity || {};
  const activeExclusion = (detail.exclusions || []).find((row) => !row.revoked_at);

  return (
    <div className="admin-panel">
      <button type="button" className="admin-button admin-button--link" onClick={onBack}>
        ← Back to search
      </button>

      <header className="admin-detail-header">
        {identity.image && <img src={identity.image} alt="" className="admin-detail-image" />}
        <div>
          <h3>{identity.description || cardId}</h3>
          <p className="admin-card-sub">
            {[identity.player, identity.sport, identity.number && `#${identity.number}`, identity.variant]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <p className="admin-card-sub"><code>{cardId}</code></p>
        </div>
      </header>

      {!detail.found && (
        <p className="admin-notice" role="status">
          This card is not in any published or staging table right now.
        </p>
      )}

      {activeExclusion && (
        <p className="admin-notice admin-notice--warning" role="status">
          Excluded by {activeExclusion.created_by || 'system'}
          {activeExclusion.reason ? ` — ${activeExclusion.reason}` : ''}.
          Restore it from the Exclusions tab.
        </p>
      )}

      <h3 className="admin-section-title">Published lists ({detail.published.length})</h3>
      {detail.published.length === 0 && (
        <p className="admin-meta-line">Not on any published list.</p>
      )}
      {detail.published.map((entry, index) => (
        <SourceSection key={entry.source_table} entry={entry} defaultOpen={index === 0} />
      ))}

      <h3 className="admin-section-title">Staging — pipeline inputs ({detail.staging.length})</h3>
      {detail.staging.length === 0 && (
        <p className="admin-meta-line">No staging row. The pulls have not seen this card.</p>
      )}
      {detail.staging.map((entry) => (
        <SourceSection key={entry.source_table} entry={entry} defaultOpen={false} />
      ))}

      <h3 className="admin-section-title">GemRate history ({detail.gemrate_history.length})</h3>
      {detail.gemrate_history.length === 0 ? (
        <p className="admin-meta-line">No archived GemRate snapshots for this card.</p>
      ) : (
        <div className="admin-table-scroll">
          <table className="admin-table admin-table--compact">
            <thead>
              <tr>
                <th scope="col">Captured</th>
                <th scope="col">Env</th>
                <th scope="col">Source</th>
                <th scope="col">Gems / Total</th>
                <th scope="col">Gem rate</th>
                <th scope="col">Raw</th>
                <th scope="col">30d PSA 10</th>
                <th scope="col">COIN</th>
              </tr>
            </thead>
            <tbody>
              {detail.gemrate_history.map((snapshot, index) => (
                <tr key={`${snapshot.captured_at}-${snapshot.source_table}-${index}`}>
                  <td>{plain(snapshot.captured_at)}</td>
                  <td>{plain(snapshot.source_env)}</td>
                  <td><code>{plain(snapshot.source_table)}</code></td>
                  <td>{plain(snapshot.card_gems)} / {plain(snapshot.card_total_grades)}</td>
                  <td>{rate(snapshot.card_gem_rate)}</td>
                  <td>{money(snapshot.raw_price)}</td>
                  <td>{money(snapshot.price_30d)}</td>
                  <td>{plain(snapshot.coin_score)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="admin-section-title">AI insights</h3>
      {detail.insights ? <RowTable row={detail.insights} /> : (
        <p className="admin-meta-line">No generated insights for this card.</p>
      )}
    </div>
  );
}

export default AdminCardDetail;
